import { useState } from 'react';

import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/teams-app-internal-analytics';
import { logException } from '@atlaskit/teams-client/sentry';
import { useOnceWhen } from '@atlassian/teams-common/use-once';

import { AnalyticsActionSubject } from '../components/analytics';
import type { Product } from '../types/product';
import { postJson } from '../utils/fetch';
import { getProductAris } from '../utils/get-product-aris';

export interface InviteCapabilitiesResponseItem {
	directInvite?: {
		mode: 'ANYONE' | 'DOMAIN_RESTRICTED' | 'NONE';
		domains?: string[];
	};
	invitePendingApproval?: {
		mode: 'ANYONE' | 'NONE';
	};
	resourceARI?: string;
	role?: string;
}

type InviteCapabilitiesPayload = {
	productARIs: string[];
};

const fetchInviteCapabilities = async ({
	products,
	cloudId,
	source,
	fireEvent,
}: {
	products: Product[];
	cloudId: string;
	source?: string;
	fireEvent: ReturnType<typeof useAnalyticsEventsNext>['fireEvent'];
}) => {
	try {
		const productARIs = getProductAris({ products, cloudId });

		const payload: InviteCapabilitiesPayload = {
			productARIs,
		};

		const result = await postJson<InviteCapabilitiesResponseItem[]>(
			'/gateway/api/v3/invitations/capabilities',
			payload,
		);

		return result;
	} catch (e) {
		fireEvent('operational.inviteCapabilitiesService.failed', {
			products,
			integration: source || AnalyticsActionSubject.TEAM_CREATE_DIALOG,
		});

		return undefined;
	}
};

export const canDirectInviteTo = ({
	product,
	response,
}: {
	product: Product;
	response: InviteCapabilitiesResponseItem[] | undefined;
}): boolean => {
	if (!response) {
		return false;
	}

	const productResponseItem = response.find((responseItem) =>
		responseItem.resourceARI?.includes(product),
	);

	if (!productResponseItem) {
		return false;
	}

	if (
		productResponseItem.directInvite?.mode === 'ANYONE' ||
		productResponseItem.directInvite?.mode === 'DOMAIN_RESTRICTED'
	) {
		return true;
	}

	return false;
};

export const useInviteCapabilities = ({
	cloudId,
	shouldRun,
	source,
}: {
	cloudId: string;
	shouldRun: boolean;
	source?: string;
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState<undefined | InviteCapabilitiesResponseItem[]>(undefined);
	const { fireEvent } = useAnalyticsEventsNext();

	useOnceWhen(!isLoading && !data && shouldRun, async () => {
		setIsLoading(true);
		try {
			const result = await fetchInviteCapabilities({
				products: ['jira-software', 'confluence'],
				cloudId,
				source,
				fireEvent,
			});
			setIsLoading(false);
			setData(result);
		} catch (error) {
			logException(error, 'useInviteCapabilitiesError');
		} finally {
			setIsLoading(false);
		}
	});

	return {
		isLoading,
		data,
		canInviteToJiraSoftware: canDirectInviteTo({ product: 'jira-software', response: data }),
		canInviteToConfluence: canDirectInviteTo({ product: 'confluence', response: data }),
	};
};
